import {
  trackRecsysItemClick,
  trackRecsysItemLastView,
  trackRecsysItemListView,
  trackRecsysItemServe,
  trackRecsysRequest,
  trackRecsysSectionLastView,
  trackRecsysSectionListView,
  trackRecsysSectionServe,
  trackRecsysSectionViewAll,
} from "../events/recsys";
import { Section } from "../../types";
import {
  getOrCreateRecsysBatchImpression,
  getRecsysImpression,
  getOrCreateRecsysImpression,
  markRecsysImpressionClicked,
  shouldEmitRecsysItemLastView,
  shouldEmitRecsysItemListView,
  shouldEmitRecsysSectionLastView,
  shouldEmitRecsysSectionListView,
  shouldEmitRecsysSectionServe,
} from "./impressionRegistry";
import {
  getLatestRecsysRequestUuidForStream,
  getRecsysRequestUuidForSectionId,
  getRecsysSectionPositionForSectionId,
  getRecsysStreamSessionIdForRequest,
  getRecsysRequestContextsForStream,
  registerRecsysRequestContext,
} from "./requestStore";
import { getOrCreateRecsysStreamSessionId } from "./streamSession";
import { CUSTOM_CARD_TYPES } from "../../components/cards/constants";
import { createClientUuid } from "../createClientUuid";

type RecsysTrackableItem = {
  entity_type?: string;
  id?: string | number;
};

type RegisterRecsysRequestInput = {
  streamId: string;
  streamKey: string;
  page: number;
  sections: Section[];
};

type RecsysSectionScope = {
  section: Section;
  sectionIndex: number;
};

type RecsysItemScope = RecsysSectionScope & {
  item: RecsysTrackableItem;
  itemIndex: number;
  trackingItemIndex?: number;
};

type RecsysItemServeBatchEntry = {
  item: RecsysTrackableItem;
  itemId: string;
  itemIndex: number;
  itemType: string;
};

type RecsysItemServeBatch = {
  entries: RecsysItemServeBatchEntry[];
  itemId: string;
  itemType: string;
  position: string;
};

type RecsysItemServeBatchMatch = {
  batch: RecsysItemServeBatch;
  entry: RecsysItemServeBatchEntry;
};

const MAX_RECSYS_ITEM_SERVE_ITEM_ID_LENGTH = 100;

function toPosition(index: number): number {
  return Math.max(index, 0);
}

function toBatchPosition(startIndex: number, endIndex: number): string {
  return `${toPosition(startIndex)}-${toPosition(endIndex)}`;
}

function toContextualItemType(context: string | undefined): string | undefined {
  if (context === "comics") return "comic";
  if (context === "releases") return "release";
  if (context === "creators") return "artist";
  if (context === "news") return "news";
  if (context === "bundle") return "bundles";
  if (context === CUSTOM_CARD_TYPES.streamLinks) return "stream_links";
  if (
    context === CUSTOM_CARD_TYPES.hero ||
    context === CUSTOM_CARD_TYPES.featuredCarousel ||
    context === CUSTOM_CARD_TYPES.featuredSeriesRoadblock
  ) {
    return "mini_banner";
  }

  return undefined;
}

function toItemType(
  item: RecsysTrackableItem,
  sectionContext?: string,
): string {
  const contextItemType = toContextualItemType(sectionContext);
  if (contextItemType) {
    return contextItemType;
  }

  const entityType = (item.entity_type ?? "").toLowerCase();
  if (entityType.includes("comic")) return "comic";
  if (entityType.includes("release")) return "release";
  if (entityType.includes("artist")) return "artist";
  if (entityType.includes("news") || entityType.includes("article")) return "news";
  if (entityType.includes("bundle")) return "bundles";
  if (entityType.includes("customcard")) return "mini_banner";
  return "comic";
}

function normalizeSectionType(sourceType: string | undefined): string {
  if (!sourceType) {
    return "unknown";
  }

  return sourceType.replace(/-/g, "_");
}

function toSectionItemType(section: Section, items: RecsysTrackableItem[]): string {
  const context = section.meta?.layout?.style?.context as string | undefined;
  const contextItemType = toContextualItemType(context);
  if (contextItemType) {
    return contextItemType;
  }

  const firstTrackableItem = items.find((item) => item.id !== undefined && item.id !== null);
  return firstTrackableItem ? toItemType(firstTrackableItem, context) : "comic";
}

type RecsysContext = {
  requestUuid: string;
  streamSessionId: string;
};

function resolveRecsysContext(
  sectionId: string | number,
): RecsysContext | undefined {
  const requestUuid = getRecsysRequestUuidForSectionId(sectionId);
  if (!requestUuid) return undefined;

  const streamSessionId = getRecsysStreamSessionIdForRequest(requestUuid);
  if (!streamSessionId) return undefined;

  return { requestUuid, streamSessionId };
}

function getSectionPositionOffset(streamId: string, page: number): number {
  return getRecsysRequestContextsForStream(streamId)
    .filter((context) => context.page < page)
    .sort((left, right) => left.page - right.page)
    .reduce((total, context) => total + context.sections.length, 0);
}

function resolveSectionPosition(
  sectionId: string | number,
  fallbackSectionIndex: number,
): number {
  return (
    getRecsysSectionPositionForSectionId(sectionId) ??
    toPosition(fallbackSectionIndex)
  );
}

function countItemsInSection(section: Section): number {
  return (
    (section.comics?.length ?? 0) +
    (section.artists?.length ?? 0) +
    (section.articles?.length ?? 0) +
    (section.releases?.length ?? 0) +
    (section.cards?.length ?? 0) +
    (section.bundles?.length ?? 0)
  );
}

function getTrackableItemsForSection(section: Section): RecsysTrackableItem[] {
  const context = section.meta?.layout?.style?.context;

  if (context === "comics") return section.comics ?? [];
  if (context === "creators") return section.artists ?? [];
  if (context === "news") return section.articles ?? [];
  if (context === "releases") return section.releases ?? [];
  if (context === "bundle") return section.bundles ?? [];
  if (
    context === CUSTOM_CARD_TYPES.hero ||
    context === CUSTOM_CARD_TYPES.featuredCarousel ||
    context === CUSTOM_CARD_TYPES.featuredSeriesRoadblock ||
    context === CUSTOM_CARD_TYPES.streamLinks
  ) {
    return section.cards ?? [];
  }

  return [
    ...(section.comics ?? []),
    ...(section.artists ?? []),
    ...(section.articles ?? []),
    ...(section.releases ?? []),
    ...(section.cards ?? []),
    ...(section.bundles ?? []),
  ];
}

function buildRecsysItemServeBatches(
  section: Section,
  items: RecsysTrackableItem[],
): RecsysItemServeBatch[] {
  const batches: RecsysItemServeBatch[] = [];
  let currentBatch: RecsysItemServeBatch | null = null;

  for (const [index, item] of items.entries()) {
    if (item.id === undefined || item.id === null) {
      continue;
    }

    const itemId = String(item.id);
    const itemType = toItemType(
      item,
      section.meta?.layout?.style?.context,
    );
    const nextEntry: RecsysItemServeBatchEntry = {
      item,
      itemId,
      itemIndex: index,
      itemType,
    };

    if (!currentBatch) {
      currentBatch = {
        entries: [nextEntry],
        itemId,
        itemType,
        position: toBatchPosition(index, index),
      };
      continue;
    }

    const nextJoinedItemId = `${currentBatch.itemId}|${itemId}`;
    const canAppendToCurrentBatch =
      currentBatch.itemType === itemType &&
      nextJoinedItemId.length <= MAX_RECSYS_ITEM_SERVE_ITEM_ID_LENGTH;

    if (!canAppendToCurrentBatch) {
      batches.push(currentBatch);
      currentBatch = {
        entries: [nextEntry],
        itemId,
        itemType,
        position: toBatchPosition(index, index),
      };
      continue;
    }

    currentBatch.entries.push(nextEntry);
    currentBatch.itemId = nextJoinedItemId;
    currentBatch.position = toBatchPosition(
      currentBatch.entries[0].itemIndex,
      index,
    );
  }

  if (currentBatch) {
    batches.push(currentBatch);
  }

  return batches;
}

function getBatchImpressionContext(
  requestUuid: string,
  section: Section,
  sectionPosition: number,
  entry: RecsysItemServeBatchEntry,
) {
  return {
    requestUuid,
    sectionId: section.id,
    sectionPosition,
    itemType: entry.itemType,
    itemId: entry.item.id!,
    itemPosition: toPosition(entry.itemIndex),
  };
}

function getBatchServeContext(
  requestUuid: string,
  section: Section,
  sectionPosition: number,
  batch: RecsysItemServeBatch,
): {
  requestUuid: string;
  sectionId: string | number;
  sectionPosition: number;
  itemType: string;
  itemId: string;
  position: string;
} {
  return {
    requestUuid,
    sectionId: section.id,
    sectionPosition,
    itemType: batch.itemType,
    itemId: batch.itemId,
    position: batch.position,
  };
}

function resolveTrackingItemIndex(scope: RecsysItemScope): number {
  return scope.trackingItemIndex ?? scope.itemIndex;
}

function ensureRecsysItemServeBatchTracked(
  section: Section,
  sectionIndex: number,
  batch: RecsysItemServeBatch,
): string | undefined {
  const ctx = resolveRecsysContext(section.id);
  if (!ctx) return undefined;

  const { requestUuid, streamSessionId } = ctx;
  const sectionPosition = resolveSectionPosition(section.id, sectionIndex);
  const batchImpression = getOrCreateRecsysBatchImpression(
    getBatchServeContext(requestUuid, section, sectionPosition, batch),
  );

  if (!batchImpression.serveSent) {
    trackRecsysItemServe({
      streamSessionId,
      requestUuid,
      sectionId: section.id,
      sectionPosition,
      itemType: batch.itemType,
      itemId: batch.itemId,
      position: batch.position,
      impressionId: batchImpression.impressionId,
    });

    for (const entry of batch.entries) {
      const impression = getOrCreateRecsysImpression(
        getBatchImpressionContext(requestUuid, section, sectionPosition, entry),
      );
      impression.impressionId = batchImpression.impressionId;
      impression.serveSent = true;
    }

    batchImpression.serveSent = true;
  }

  return batchImpression.impressionId;
}

function findRecsysItemServeBatchForItem(
  section: Section,
  items: RecsysTrackableItem[],
  itemIndex: number,
  itemId: string | number,
): RecsysItemServeBatchMatch | undefined {
  const targetItemId = String(itemId);
  const batches = buildRecsysItemServeBatches(section, items);

  for (const batch of batches) {
    const exactEntry = batch.entries.find(
      (entry) =>
        entry.itemIndex === itemIndex &&
        entry.itemId === targetItemId,
    );
    if (exactEntry) {
      return { batch, entry: exactEntry };
    }
  }

  return undefined;
}

export function registerAndTrackRecsysRequest(
  input: RegisterRecsysRequestInput,
): string {
  const requestUuid = createClientUuid();
  const streamSessionId = getOrCreateRecsysStreamSessionId(input.streamId);
  const sectionCount = input.sections.length;
  const totalItemCount = input.sections.reduce(
    (total, section) => total + countItemsInSection(section),
    0,
  );

  trackRecsysRequest({
    streamSessionId,
    requestUuid,
    streamKey: input.streamKey,
    endpoint: `/v1/cms/${input.streamId}/sections?p=${input.page}`,
    page: input.page,
    sectionCount,
    totalItemCount,
  });

  const positionOffset = getSectionPositionOffset(input.streamId, input.page);

  registerRecsysRequestContext({
    streamId: input.streamId,
    streamKey: input.streamKey,
    streamSessionId,
    requestUuid,
    page: input.page,
    sections: input.sections.map((section, index) => ({
      sectionId: String(section.id),
      itemCount: countItemsInSection(section),
      sectionPosition: positionOffset + index,
    })),
  });

  input.sections.forEach((section, index) => {
    trackRecsysSectionAndItemServe(
      section,
      positionOffset + index,
      getTrackableItemsForSection(section),
    );
  });

  return requestUuid;
}

export function trackRecsysSectionAndItemServe(
  section: Section,
  sectionIndex: number,
  items: RecsysTrackableItem[],
): void {
  const ctx = resolveRecsysContext(section.id);
  if (!ctx) return;

  const { requestUuid, streamSessionId } = ctx;
  const sectionPosition = resolveSectionPosition(section.id, sectionIndex);
  const sectionItemType = toSectionItemType(section, items);

  if (shouldEmitRecsysSectionServe(requestUuid, section.id)) {
    trackRecsysSectionServe({
      streamSessionId,
      requestUuid,
      sectionId: section.id,
      sectionPosition,
      sectionType: normalizeSectionType(section.source_type),
      isAuto: section.source_type !== "manual",
      sectionTitle: section.display_name,
      itemType: sectionItemType,
      itemCount: items.length,
    });
  }

  for (const batch of buildRecsysItemServeBatches(section, items)) {
    ensureRecsysItemServeBatchTracked(section, sectionIndex, batch);
  }
}

export function trackRecsysVisibleItemServe(scope: RecsysItemScope): void {
  if (scope.item.id === undefined || scope.item.id === null) {
    return;
  }

  const batchMatch = findRecsysItemServeBatchForItem(
    scope.section,
    getTrackableItemsForSection(scope.section),
    resolveTrackingItemIndex(scope),
    scope.item.id,
  );
  if (!batchMatch) return;

  ensureRecsysItemServeBatchTracked(
    scope.section,
    scope.sectionIndex,
    batchMatch.batch,
  );
}

export function trackRecsysSectionViewAllClick(scope: RecsysSectionScope): void {
  const ctx = resolveRecsysContext(scope.section.id);
  if (!ctx) return;

  const { requestUuid, streamSessionId } = ctx;

  trackRecsysSectionViewAll({
    streamSessionId,
    requestUuid,
    sectionId: scope.section.id,
    sectionPosition: resolveSectionPosition(scope.section.id, scope.sectionIndex),
  });
}

export function trackRecsysCardItemClick(
  scope: RecsysItemScope,
): string | undefined {
  if (scope.item.id === undefined || scope.item.id === null) {
    return undefined;
  }

  const ctx = resolveRecsysContext(scope.section.id);
  if (!ctx) return undefined;

  const { requestUuid, streamSessionId } = ctx;
  const sectionPosition = resolveSectionPosition(scope.section.id, scope.sectionIndex);
  const itemId = scope.item.id;
  const batchMatch = findRecsysItemServeBatchForItem(
    scope.section,
    getTrackableItemsForSection(scope.section),
    resolveTrackingItemIndex(scope),
    itemId,
  );
  if (!batchMatch) return undefined;

  const { batch, entry } = batchMatch;
  const position = toPosition(entry.itemIndex);
  const itemType = entry.itemType;

  ensureRecsysItemServeBatchTracked(scope.section, scope.sectionIndex, batch);

  const impression =
    getRecsysImpression({
      requestUuid,
      sectionId: scope.section.id,
      sectionPosition,
      itemType,
      itemId,
      itemPosition: position,
    }) ??
    getOrCreateRecsysImpression({
      requestUuid,
      sectionId: scope.section.id,
      sectionPosition,
      itemType,
      itemId,
      itemPosition: position,
    });

  markRecsysImpressionClicked({
    requestUuid,
    sectionId: scope.section.id,
    sectionPosition,
    itemType,
    itemId,
    itemPosition: position,
  });

  trackRecsysItemClick({
    streamSessionId,
    requestUuid,
    impressionId: impression.impressionId,
    sectionId: scope.section.id,
    sectionPosition,
    itemType,
    itemId,
    position,
    firstVisibleAtMs: impression.firstVisibleAtMs,
  });

  return impression.impressionId;
}

function resolveRecsysContextForStream(
  streamId: string | undefined,
  sectionId?: string | number,
): RecsysContext | undefined {
  const requestUuid =
    (sectionId !== undefined
      ? getRecsysRequestUuidForSectionId(sectionId)
      : undefined) ?? getLatestRecsysRequestUuidForStream(streamId);
  if (!requestUuid) return undefined;

  const streamSessionId = getRecsysStreamSessionIdForRequest(requestUuid);
  if (!streamSessionId) return undefined;

  return { requestUuid, streamSessionId };
}

export function trackRecsysSectionListViewFromVisibleIndex(
  streamId: string | undefined,
  lastVisibleSectionIndex: number,
  lastVisibleSectionId?: string | number,
): void {
  const ctx = resolveRecsysContextForStream(streamId, lastVisibleSectionId);
  if (!ctx) return;

  const { requestUuid, streamSessionId } = ctx;

  if (!shouldEmitRecsysSectionListView(requestUuid, streamSessionId)) {
    return;
  }

  trackRecsysSectionListView({
    streamSessionId,
    requestUuid,
    lastVisiblePosition: toPosition(lastVisibleSectionIndex),
  });
}

export function trackRecsysSectionLastViewFromVisibleIndex(
  streamId: string | undefined,
  lastVisibleSectionIndex: number,
  lastVisibleSectionId?: string | number,
): void {
  const ctx = resolveRecsysContextForStream(streamId, lastVisibleSectionId);
  if (!ctx) return;

  const { requestUuid, streamSessionId } = ctx;
  const lastVisiblePosition = toPosition(lastVisibleSectionIndex);

  if (!shouldEmitRecsysSectionLastView(requestUuid, lastVisiblePosition)) {
    return;
  }

  trackRecsysSectionLastView({
    streamSessionId,
    requestUuid,
    lastVisiblePosition,
  });
}

export function trackRecsysItemListViewForSection(
  section: Section,
  sectionIndex: number,
  lastVisibleItemIndex: number,
): void {
  const ctx = resolveRecsysContext(section.id);
  if (!ctx) return;

  const { requestUuid, streamSessionId } = ctx;

  if (!shouldEmitRecsysItemListView(requestUuid, section.id)) {
    return;
  }

  trackRecsysItemListView({
    streamSessionId,
    requestUuid,
    sectionId: section.id,
    sectionPosition: toPosition(sectionIndex),
    lastVisiblePosition: toPosition(lastVisibleItemIndex),
  });
}

export function trackRecsysItemLastViewForSection(
  section: Section,
  sectionIndex: number,
  lastVisibleItemIndex: number,
): void {
  const ctx = resolveRecsysContext(section.id);
  if (!ctx) return;

  const { requestUuid, streamSessionId } = ctx;

  if (!shouldEmitRecsysItemLastView(requestUuid, section.id)) {
    return;
  }

  trackRecsysItemLastView({
    streamSessionId,
    requestUuid,
    sectionId: section.id,
    sectionPosition: toPosition(sectionIndex),
    lastVisiblePosition: toPosition(lastVisibleItemIndex),
  });
}
